import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
// Components
import { AutoComplete, Col, Form, message, Row } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import StepsHeading from './StepsHeading';
import {
  AddItemButton,
  CustomButton,
  CustomInput,
  EllipsisTooltip,
  CustomModal,
  PolygonFenceMap,
} from '../../../components';
import { useToken } from '../../../common/helper';
import { useSpin } from '../../../components/SpinContext.jsx';
import {
  AddLocations,
  GetLocationsWithPaginationAndSearch,
} from '../../../apis/onboarding.js';
import { ClearLocationPolygon } from '../../../apis/settings.js';
import EditLocation from '../../../assets/images/edit_location.svg';
import { UndoIcon } from '@/assets/icons/icons.js';
import usePolygonEditSession from '@/hooks/usePolygonEditSession';
import PhoneInput from 'react-phone-input-2';
import { isValidPhoneNumber } from 'libphonenumber-js';
import useUnauthorize from '../../../hooks/useUnauthorize';

const Locations = forwardRef(({ onSuccess }, ref) => {
  const [locationForm] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalName, setModalName] = useState('');
  const [selectedLatLng, setSelectedLatLng] = useState(null);
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const { showSpin, hideSpin } = useSpin();
  const [options, setOptions] = useState([]);
  const [polygonIndex, setPolygonIndex] = useState(-1);
  const polygonSession = usePolygonEditSession();
  const [isRemovingPolygon, setIsRemovingPolygon] = useState(false);
  const [patchedArray, setPatchedArray] = useState([]);
  //when polygon modal gets submit
  const onModalConfirm = async () => {
    if (
      polygonIndex < 0 ||
      locationForm.getFieldValue('locationList').length === 0
    ) {
      return;
    }

    if (polygonSession.isEmpty) {
      // Only a saved location (a real id) has anything to clear server-side.
      const locationList = locationForm.getFieldValue('locationList');
      const row = locationList?.[polygonIndex];

      setIsRemovingPolygon(true);
      try {
        if (row?.id) {
          const res = await ClearLocationPolygon(token, row.id);
          if (res?.data?.data?.is_success === false) {
            message.error(res.data.data.message || 'Failed to remove polygon');
            return;
          }
        }
        message.success('Polygon removed.');
        setModalOpen(false);
        setPolygonIndex(-1);
      } catch (error) {
        unauthorize(error);
        message.error('Failed to remove polygon. Please try again.');
      } finally {
        setIsRemovingPolygon(false);
      }
      return;
    }

    let shape = locationForm.getFieldValue([
      'locationList',
      polygonIndex,
      'polygon',
    ]);
    if (polygonSession.isClosed && shape?.length >= 3) {
      message.success('Polygon created');
      setModalOpen(false);
      setPolygonIndex(-1);
    } else {
      message.error('Please close the polygon before saving.');
    }
  };

  const onValuesChange = (_) => {};

  const [locationsList, setLocationsList] = useState([]);
  const loadMoreRef = useRef(null);
  const hasUserScrolledRef = useRef(false);
  const isFetchingMoreRef = useRef(false);
  const pageRef = useRef(1);
  const totalPagesRef = useRef(1);

  const renderFieldLabel = (index, text) =>
    index === 0 ? (
      <EllipsisTooltip
        text={text}
        className='onboarding-location-field-label'
      />
    ) : (
      ''
    );

  const fetchLocations = useCallback(
    (checked, page, search, isLoadMore = false) => {
      if (isLoadMore) {
        if (isFetchingMoreRef.current) return;

        isFetchingMoreRef.current = true;
      }

      showSpin();

      GetLocationsWithPaginationAndSearch(token, checked, page, search)
        .then((res) => {
          const data = res?.data?.data;
          if (data?.is_success) {
            const newItems = data?.data?.data || [];

            setLocationsList((prev) =>
              isLoadMore ? [...prev, ...newItems] : newItems
            );

            pageRef.current = page;
            totalPagesRef.current = data?.page_count || 1;
          } else {
            message.error(data?.message || 'Failed to fetch locations');
          }
        })
        .catch((error) => {
          unauthorize(error);
          message.error('Failed to fetch locations');
        })
        .finally(() => {
          hideSpin();
          if (isLoadMore) {
            isFetchingMoreRef.current = false;
          }
        });
    },
    [hideSpin, showSpin, token, unauthorize]
  );

  useEffect(() => {
    pageRef.current = 1;
    totalPagesRef.current = 1;
    isFetchingMoreRef.current = false;
    fetchLocations(false, 1, '', false);
  }, [fetchLocations]);

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    const scrollRoot = sentinel?.closest('.onboarding-steps-content');

    if (!sentinel || !scrollRoot) return undefined;

    hasUserScrolledRef.current = false;

    const handleScroll = () => {
      hasUserScrolledRef.current = scrollRoot.scrollTop > 0;
    };

    scrollRoot.addEventListener('scroll', handleScroll, { passive: true });

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          hasUserScrolledRef.current &&
          !isFetchingMoreRef.current &&
          pageRef.current < totalPagesRef.current
        ) {
          const next = pageRef.current + 1;
          fetchLocations(false, next, '', true);
        }
      },
      {
        root: scrollRoot,
        rootMargin: '80px 0px',
        threshold: 0,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
      scrollRoot.removeEventListener('scroll', handleScroll);
    };
  }, [fetchLocations]);

  //setting data from store
  useEffect(() => {
    if (locationsList) {
      if (locationsList.length > 0) {
        let arr = locationsList;
        if (arr.length > 0) {
          const newArray = arr.map((item) => ({
            ...item, // Spread the existing properties of the object
            contact_name: item.key_contact_name,
            contact_email: item.key_contact_email,
            contact_phone_number: item.key_contact_phone_number, // Add key_name as 'name'
          }));
          setPatchedArray(newArray);
        }
      }
    }
  }, [locationsList]);

  useEffect(() => {
    if (patchedArray.length > 0) {
      locationForm.setFieldsValue({
        locationList: patchedArray,
      });
    }
  }, [locationForm, patchedArray]);

  //ADD Location
  const submitLocations = (model) => {
    AddLocations(token, model)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data?.data?.is_success) {
            onSuccess();
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  };

  useImperativeHandle(ref, () => ({
    completeStep, // Expose the submitProducts method to the parent
  }));

  //when modal opens
  const openMapModal = (index) => {
    setModalOpen(true);
    setModalName('Draw Polygon');
    if (locationForm.getFieldValue('locationList').length > 0) {
      setSelectedLatLng({
        lat: parseFloat(
          locationForm.getFieldValue('locationList')[index].latitude
        ),
        lng: parseFloat(
          locationForm.getFieldValue('locationList')[index].longitude
        ),
      });
      let shape = locationForm.getFieldValue([
        'locationList',
        index,
        'polygon',
      ]);
      polygonSession.load(shape || [], (shape || []).length >= 3);
    }
    setPolygonIndex(index);
  };

  const completeStep = () => {
    locationForm
      .validateFields()
      .then((values) => {
        if (values && Object.keys(values).length > 0) {
          let model = locationForm.getFieldValue('locationList');
          if (model && model?.length > 0) {
            submitLocations(model);
          }
        } else {
          message.error('Please add at least one location');
        }
      })
      .catch(() => {});
  };

  // Keeps the row's Form.List field (submitted to the backend) in sync
  // with the hook's state (drives the map).
  const syncPolygonToForm = (index, nextPolygon) => {
    const locationList = locationForm.getFieldValue('locationList');
    if (locationList && locationList.length > 0 && index > -1) {
      const updatedLocationList = [...locationList];
      updatedLocationList[index] = {
        ...updatedLocationList[index],
        polygon: nextPolygon,
      };
      locationForm.setFieldsValue({
        locationList: updatedLocationList,
      });
    }
  };

  //runs on every vertex change while drawing, not just on completion.
  const onPolygonChange = (val) => {
    const nextPolygon = val || [];
    polygonSession.setPolygon(nextPolygon);
    syncPolygonToForm(polygonIndex, nextPolygon);
  };

  const handlePolygonUndo = () => {
    const nextPolygon = polygonSession.polygon.slice(0, -1);
    polygonSession.undo();
    syncPolygonToForm(polygonIndex, nextPolygon);
  };

  //below 3 functions are relative to address search
  const handleAddressPlaceSelect = (place, index) => {
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    setLatLngInLocationList(lat, lng, index);

    const locationList = locationForm.getFieldValue('locationList');
    if (locationList && locationList?.length > 0) {
      const updatedLocationList = [...locationList];
      updatedLocationList[index].address = place.formatted_address;
      locationForm.setFieldsValue({
        locationList: updatedLocationList,
      });
    }
  };

  const setLatLngInLocationList = (lat, lng, ind) => {
    const locationList = locationForm.getFieldValue('locationList');
    if (locationList && locationList?.length > 0) {
      const updatedLocationList = [...locationList];
      updatedLocationList[ind].latitude = lat;
      updatedLocationList[ind].longitude = lng;
      locationForm.setFieldsValue({
        locationList: updatedLocationList,
      });
    }
  };

  const handleAddressChange = (value, index) => {
    if (!value) {
      setLatLngInLocationList('', '', index);
    }
  };

  // CustomModal keeps its map mounted even while closed, and selectedLatLng
  // starts null — fall back to a default center to avoid panning to NaN.
  const hasValidSelectedLatLng =
    selectedLatLng &&
    Number.isFinite(selectedLatLng.lat) &&
    Number.isFinite(selectedLatLng.lng);
  const polygonMapCenter = hasValidSelectedLatLng
    ? selectedLatLng
    : { lat: 37.7749, lng: -122.4194 };

  return (
    <>
      <div className='onboarding-locations-step'>
        <div className='large-steps-content'>
          <StepsHeading
            heading='Save your shipping locations'
            text="Save information about where you're shipping to & from so we can provide this information to you when you need it, like when you might need to call a location to reschedule a delivery. You'll be able to add more than one contact per location and edit these details later."
          />
          <Form
            form={locationForm}
            name='locationsFormList'
            layout='vertical'
            onValuesChange={onValuesChange}
          >
            <Form.List
              name='locationList' // Ensure name is provided for the list
              initialValue={[
                {
                  name: '',
                  address: '',
                  contact_name: '',
                  contact_email: '',
                  contact_phone_number: '',
                  map: false,
                  latitude: '',
                  longitude: '',
                  polygon: [],
                  contacts: [
                    { key: '', name: '', email: '', phone_number: '' },
                  ],
                },
              ]}
            >
              {(fields, { add, remove }) => (
                <Row>
                  <Col span={24}>
                    <>
                      {fields.map((field, index) => (
                        <Row
                          className='onboarding-location-row'
                          gutter={24}
                          style={{
                            gridGap: '16px 0',
                            padding: '28px 0px',
                            borderBottom: '1px solid rgba(5, 5, 5, 0.1)',
                          }}
                          key={index}
                        >
                          <Col xxl={4} xl={4} lg={5} md={6} sm={12} xs={24}>
                            <Form.Item
                              label={renderFieldLabel(index, 'Location Name')}
                              name={[index, 'name']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Please enter location name',
                                },
                                {
                                  pattern:
                                    /^(?=.*[A-Za-zÀ-ÖØ-öø-ÿ])[A-Za-zÀ-ÖØ-öø-ÿ0-9\s.'\-()]{2,100}$/,
                                  message:
                                    'Name must include at least one letter and only valid characters',
                                },
                              ]}
                              style={{ marginBottom: '0' }}
                              labelCol={{
                                style: {
                                  marginBottom: index === 0 ? '10px' : '0', // Add space between label and input for index 0
                                },
                              }}
                            >
                              <CustomInput
                                placeholder='City Hospital'
                                height='36px'
                                border='0px'
                                bgColor='var(--light-gray)'
                                textColor='var(--text-secondary)'
                                borderRadius='8px'
                                placeholderColor='var(--text-secondary)'
                                lineHeight='38px'
                              />
                            </Form.Item>
                          </Col>

                          <Col xxl={6} xl={6} lg={8} md={9} sm={12} xs={24}>
                            <Form.Item
                              label={renderFieldLabel(index, 'Address')}
                              name={[index, 'address']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Please enter address',
                                },
                              ]}
                              style={{ marginBottom: '0' }}
                              labelCol={{
                                style: {
                                  marginBottom: index === 0 ? '10px' : '0',
                                },
                              }}
                            >
                              <div
                                className='onboarding-location-address'
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-end',
                                  minWidth: 0,
                                  width: '100%',
                                }}
                              >
                                <AutoComplete
                                  value={locationForm.getFieldValue([
                                    'locationList',
                                    index,
                                    'address',
                                  ])}
                                  style={{
                                    flexGrow: 1,
                                    minWidth: 0,
                                    width: 0,
                                    boxShadow: 'none',
                                    height: '36px',
                                    backgroundColor: 'var(--light-gray)',
                                    color: 'var(--text-secondary)',
                                    borderRadius: '8px',
                                  }}
                                  options={options}
                                  onSelect={(value) => {
                                    if (!value) {
                                      console.error('Invalid place ID');
                                      return;
                                    }

                                    const serviceDiv =
                                      document.createElement('div');
                                    const service =
                                      new window.google.maps.places.PlacesService(
                                        serviceDiv
                                      );

                                    service.getDetails(
                                      { placeId: value },
                                      (place, status) => {
                                        if (
                                          status ===
                                          window.google.maps.places
                                            .PlacesServiceStatus.OK
                                        ) {
                                          // Place details fetched successfully
                                          handleAddressPlaceSelect(
                                            place,
                                            index
                                          );
                                        } else {
                                          // Log error if the status is not OK
                                          console.error(
                                            'Failed to fetch place details:',
                                            status
                                          );
                                        }
                                      }
                                    );
                                  }}
                                  onSearch={(text) => {
                                    if (!text) {
                                      console.error('Empty search text');
                                      return;
                                    }

                                    const service =
                                      new window.google.maps.places.AutocompleteService();

                                    service.getPlacePredictions(
                                      { input: text },
                                      (predictions, status) => {
                                        if (
                                          status ===
                                            window.google.maps.places
                                              .PlacesServiceStatus.OK &&
                                          predictions
                                        ) {
                                          setOptions(
                                            predictions.map((prediction) => ({
                                              label: prediction.description,
                                              value: prediction.place_id,
                                            }))
                                          );
                                        } else {
                                          // Log error if the status is not OK
                                          console.error(
                                            'Failed to fetch predictions:',
                                            status
                                          );
                                        }
                                      }
                                    );
                                  }}
                                  onChange={(value) =>
                                    handleAddressChange(value, index)
                                  }
                                  placeholder='Start typing an address...'
                                />

                                <div
                                  className='onboarding-location-map-action'
                                  style={{
                                    height: 'fit-content',
                                    marginLeft: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                  }}
                                >
                                  <CustomButton
                                    isFormValid={
                                      !(
                                        locationForm.getFieldValue([
                                          'locationList',
                                          index,
                                          'latitude',
                                        ]) &&
                                        locationForm.getFieldValue([
                                          'locationList',
                                          index,
                                          'longitude',
                                        ])
                                      )
                                    }
                                    image={EditLocation}
                                    iconPosition={'left'}
                                    border='none'
                                    width='36px'
                                    height='36px'
                                    borderRadius='50%'
                                    bgColor='#f5f5f5'
                                    color='var(--text-primary)'
                                    boxShadow='none'
                                    onClick={() => openMapModal(index)}
                                    padding={'0'}
                                  />
                                </div>
                              </div>
                            </Form.Item>
                          </Col>

                          <Form.List name={[field.name, 'contacts']}>
                            {(contactFields, { add }) => {
                              if (contactFields.length === 0) {
                                add();
                              }

                              const contactValues =
                                locationForm.getFieldValue([
                                  'locationList',
                                  field.name,
                                  'contacts',
                                ]) || [];

                              const firstPrimaryIndex = contactValues.findIndex(
                                (contact) =>
                                  contact?.is_primary_contact === true
                              );
                              const newarry =
                                firstPrimaryIndex !== -1
                                  ? contactFields[firstPrimaryIndex]
                                  : contactFields[0] || {};

                              const displayedContact = newarry
                                ? [newarry]
                                : [{}];

                              return displayedContact.map((contactField) => (
                                <>
                                  <Col
                                    xxl={4}
                                    xl={4}
                                    lg={5}
                                    md={6}
                                    sm={12}
                                    xs={24}
                                  >
                                    <Form.Item
                                      label={renderFieldLabel(
                                        index,
                                        'Key Contact Name'
                                      )}
                                      name={[contactField.name, 'name']}
                                      rules={[
                                        {
                                          required: true,
                                          message: 'Please enter contact name',
                                        },
                                        {
                                          pattern: /^[A-Za-z\s'-]+$/,
                                          message:
                                            'Name must contain only letters',
                                        },
                                      ]}
                                      style={{ marginBottom: '0' }}
                                      labelCol={{
                                        style: {
                                          marginBottom:
                                            index === 0 ? '10px' : '0',
                                        },
                                      }}
                                    >
                                      <CustomInput
                                        type='text'
                                        placeholder='e.g. Rory Lee'
                                        height='36px'
                                        border='0px'
                                        bgColor='var(--light-gray)'
                                        textColor='var(--text-secondary)'
                                        borderRadius='8px'
                                        placeholderColor='var(--text-secondary)'
                                        lineHeight='38px'
                                      />
                                    </Form.Item>
                                  </Col>

                                  <Col
                                    xxl={4}
                                    xl={4}
                                    lg={5}
                                    md={6}
                                    sm={12}
                                    xs={24}
                                  >
                                    <Form.Item
                                      label={renderFieldLabel(
                                        index,
                                        'Key Contact Email'
                                      )}
                                      name={[contactField.name, 'email']}
                                      rules={[
                                        {
                                          required: true,
                                          message: 'Please enter contact email',
                                        },
                                        {
                                          type: 'email',
                                          message: 'Enter Valid Email Only',
                                        },
                                      ]}
                                      style={{ marginBottom: '0' }}
                                      labelCol={{
                                        style: {
                                          marginBottom:
                                            index === 0 ? '10px' : '0',
                                        },
                                      }}
                                    >
                                      <CustomInput
                                        placeholder='e.g. name@abc.com'
                                        height='36px'
                                        border='0px'
                                        bgColor='var(--light-gray)'
                                        textColor='var(--text-secondary)'
                                        borderRadius='8px'
                                        placeholderColor='var(--text-secondary)'
                                        lineHeight='38px'
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col
                                    xxl={5}
                                    xl={5}
                                    lg={5}
                                    md={6}
                                    sm={12}
                                    xs={24}
                                  >
                                    <Form.Item
                                      label={renderFieldLabel(
                                        index,
                                        'Key Contact Phone'
                                      )}
                                      name={[contactField.name, 'phone_number']}
                                      rules={[
                                        {
                                          validator: (_, value) => {
                                            if (!value || value.trim() === '') {
                                              return Promise.resolve(); // Optional
                                            }
                                            try {
                                              if (isValidPhoneNumber(value)) {
                                                return Promise.resolve();
                                              } else {
                                                return Promise.reject(
                                                  new Error(
                                                    'Invalid phone number'
                                                  )
                                                );
                                              }
                                            } catch {
                                              return Promise.reject(
                                                new Error(
                                                  'Invalid phone number'
                                                )
                                              );
                                            }
                                          },
                                        },
                                      ]}
                                      style={{ marginBottom: '0' }}
                                      labelCol={{
                                        style: {
                                          marginBottom:
                                            index === 0 ? '10px' : '0',
                                        },
                                      }}
                                    >
                                      <PhoneInput
                                        country={'us'}
                                        value={locationForm.getFieldValue([
                                          'locationList',
                                          field.name,
                                          'contacts',
                                          contactField.name,
                                          'phone_number',
                                        ])}
                                        onChange={(
                                          value,
                                          countryData,
                                          e,
                                          formattedValue
                                        ) => {
                                          locationForm.setFieldsValue({
                                            locationList: locationForm
                                              .getFieldValue('locationList')
                                              .map((item, idx) => {
                                                if (idx === field.name) {
                                                  return {
                                                    ...item,
                                                    contacts: item.contacts.map(
                                                      (contact, contactIdx) => {
                                                        if (
                                                          contactIdx ===
                                                          contactField.name
                                                        ) {
                                                          return {
                                                            ...contact,
                                                            phone_number:
                                                              formattedValue,
                                                          };
                                                        }
                                                        return contact;
                                                      }
                                                    ),
                                                  };
                                                }
                                                return item;
                                              }),
                                          });
                                        }}
                                      />
                                    </Form.Item>
                                  </Col>
                                </>
                              ));
                            }}
                          </Form.List>

                          <Col span={1} className='location-action-btn'>
                            <Form.Item
                              label={'action'}
                              name={[index, 'action']}
                              style={{ marginBottom: '0' }}
                              labelCol={{
                                style: {
                                  marginBottom: index === 0 ? '10px' : '0',
                                  display: index === 0 ? 'auto' : 'none',
                                  visibility: 'hidden',
                                },
                              }}
                            >
                              <CloseOutlined onClick={() => remove(index)} />
                            </Form.Item>
                          </Col>
                          {/* Hidden fields */}
                          <Form.Item
                            label=''
                            name={[index, 'latitude']}
                            style={{ marginBottom: '0', display: 'none' }}
                          ></Form.Item>
                          <Form.Item
                            label=''
                            name={[index, 'longitude']}
                            style={{ marginBottom: '0', display: 'none' }}
                          ></Form.Item>
                          <Form.Item
                            label=''
                            name={[index, 'polygon']}
                            style={{ marginBottom: '0', display: 'none' }}
                          ></Form.Item>
                          <Form.Item
                            label=''
                            name={[index, 'contacts']}
                            style={{ marginBottom: '0', display: 'none' }}
                          ></Form.Item>
                        </Row>
                      ))}
                    </>
                  </Col>
                  <Col
                    span={18}
                    offset={0}
                    className='info-div-add remove-margin-xs'
                  >
                    <AddItemButton
                      text='Add Another'
                      onClick={() => add({ contacts: [{}] })}
                    />
                  </Col>
                  <Col span={24}>
                    <div
                      ref={loadMoreRef}
                      className='onboarding-locations-load-more'
                    />
                  </Col>
                </Row>
              )}
            </Form.List>
          </Form>
        </div>
      </div>

      <CustomModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        modalTitle={`${modalName}`}
        modalText=''
        modalSize={680}
        className='onboarding-polygon-modal'
        centered={false}
        buttonText1={'Cancel'}
        buttonText2={'Submit'}
        isFormValid={isRemovingPolygon}
        onConfirmClick={onModalConfirm}
      >
        <div className='polygon-map-shell'>
          <PolygonFenceMap
            center={polygonMapCenter}
            polygon={polygonSession.polygon}
            closed={polygonSession.isClosed}
            resetTrigger={polygonSession.resetTrigger}
            onPolygonChange={onPolygonChange}
            onClosedChange={polygonSession.setIsClosed}
          />
          {polygonSession.polygon.length > 0 && (
            <button
              type='button'
              className='polygon-map-undo-btn'
              onClick={handlePolygonUndo}
            >
              <UndoIcon size={12} />
              <span>Undo</span>
            </button>
          )}
        </div>
      </CustomModal>
    </>
  );
});

Locations.displayName = 'Locations';
export default Locations;
